import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { FiX, FiPlus, FiTrash2, FiMenu } from 'react-icons/fi';
import {
  DndContext,
  DragEndEvent,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Pipeline,
  Fund,
  UpdatePipelineInput,
  PipelineStage,
  STAGE_COLORS,
} from '../../types/fundraise';

// Type-safe icon wrappers
const CloseIcon = FiX as React.FC<React.SVGProps<SVGSVGElement>>;
const PlusIcon = FiPlus as React.FC<React.SVGProps<SVGSVGElement>>;
const TrashIcon = FiTrash2 as React.FC<React.SVGProps<SVGSVGElement>>;
const MenuIcon = FiMenu as React.FC<React.SVGProps<SVGSVGElement>>;

interface EditPipelineModalProps {
  pipeline: Pipeline;
  funds: Fund[];
  onClose: () => void;
  onSubmit: (id: string, input: UpdatePipelineInput) => Promise<void>;
}

interface SortableStageItemProps {
  stage: PipelineStage;
  fundCount: number;
  onDelete: () => void;
  canDelete: boolean;
}

const SortableStageItem: React.FC<SortableStageItemProps> = ({
  stage,
  fundCount,
  onDelete,
  canDelete,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: stage.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="su-custom-stage-item">
      <button
        type="button"
        className="su-custom-stage-drag"
        {...attributes}
        {...listeners}
      >
        <MenuIcon width={14} height={14} />
      </button>
      <span
        className="su-custom-stage-dot"
        style={{ backgroundColor: STAGE_COLORS[stage.color] }}
      />
      <span className="su-custom-stage-name">{stage.name}</span>
      <span className="su-custom-stage-count">{fundCount} funds</span>
      <button
        type="button"
        className="su-custom-stage-delete"
        onClick={onDelete}
        disabled={!canDelete}
        style={{ opacity: canDelete ? 1 : 0.3 }}
        title={canDelete ? 'Delete stage' : 'Cannot delete stage with funds'}
      >
        <TrashIcon width={14} height={14} />
      </button>
    </div>
  );
};

const EditPipelineModal: React.FC<EditPipelineModalProps> = ({
  pipeline,
  funds,
  onClose,
  onSubmit,
}) => {
  const [name, setName] = useState(pipeline.name);
  const [targetAmount, setTargetAmount] = useState(pipeline.target_amount.toString());
  const [stages, setStages] = useState<PipelineStage[]>(pipeline.stages || []);
  const [newStageName, setNewStageName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const colorOptions = Object.keys(STAGE_COLORS);

  // Count funds per stage
  const fundCountByStage = React.useMemo(() => {
    const counts: Record<string, number> = {};
    stages.forEach((s) => {
      counts[s.id] = 0;
    });
    funds.forEach((f) => {
      if (counts[f.stage] !== undefined) {
        counts[f.stage]++;
      }
    });
    return counts;
  }, [stages, funds]);

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isSubmitting) {
      onClose();
    }
  };

  // Handle Escape key to close modal
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, isSubmitting]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = stages.findIndex((s) => s.id === active.id);
      const newIndex = stages.findIndex((s) => s.id === over.id);

      setStages(arrayMove(stages, oldIndex, newIndex));
    }
  };

  const handleAddStage = () => {
    if (!newStageName.trim()) return;

    const id = newStageName.toLowerCase().replace(/\s+/g, '_');

    // Check for duplicate ID (derived from name)
    if (stages.some((s) => s.id === id)) {
      setError('A stage with a similar name already exists (names that differ only in case or spacing are not allowed)');
      return;
    }

    const colorIndex = stages.length % colorOptions.length;

    setStages([
      ...stages,
      {
        id,
        name: newStageName.trim(),
        color: colorOptions[colorIndex],
      },
    ]);
    setNewStageName('');
    setError(null);
  };

  const handleRemoveStage = (stageId: string) => {
    // Check if stage has funds
    if (fundCountByStage[stageId] > 0) {
      setError('Cannot delete a stage that contains funds. Move or delete the funds first.');
      return;
    }

    setStages(stages.filter((s) => s.id !== stageId));
    setError(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStage();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!name.trim()) {
      setError('Pipeline name is required');
      return;
    }

    if (stages.length === 0) {
      setError('Pipeline must have at least one stage');
      return;
    }

    setIsSubmitting(true);

    try {
      const input: UpdatePipelineInput = {
        name: name.trim(),
        target_amount: targetAmount ? (parseInt(targetAmount, 10) || 0) : 0,
        stages,
      };

      await onSubmit(pipeline.id, input);
      onClose();
    } catch (err: any) {
      setError(err?.message || 'Failed to update pipeline');
    } finally {
      setIsSubmitting(false);
    }
  };

  const modal = (
    <div className="su-fundraise-modal-overlay" onClick={handleOverlayClick}>
      <div className="su-fundraise-modal" role="dialog" aria-modal="true" aria-labelledby="edit-pipeline-title">
        <div className="su-fundraise-modal-header">
          <div>
            <h2 id="edit-pipeline-title" className="su-fundraise-modal-title">Edit Pipeline</h2>
          </div>
          <button className="su-fundraise-modal-close" onClick={onClose} aria-label="Close modal">
            <CloseIcon width={20} height={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="su-fundraise-modal-body">
            {error && (
              <div
                style={{
                  color: '#f87171',
                  fontSize: '14px',
                  marginBottom: '16px',
                  padding: '12px',
                  backgroundColor: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                }}
              >
                {error}
              </div>
            )}

            <div className="su-fundraise-form-row">
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label className="su-fundraise-label">Pipeline Name</label>
                <input
                  type="text"
                  className="su-fundraise-input"
                  placeholder="e.g., Series A Round"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="su-fundraise-form-group" style={{ marginBottom: 0 }}>
                <label className="su-fundraise-label">Target Amount ($)</label>
                <input
                  type="number"
                  className="su-fundraise-input"
                  placeholder="5000000"
                  value={targetAmount}
                  onChange={(e) => setTargetAmount(e.target.value)}
                  min="0"
                />
              </div>
            </div>

            <div className="su-fundraise-form-group">
              <label className="su-fundraise-label">Stages</label>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={stages.map((s) => s.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="su-custom-stages-list">
                    {stages.map((stage) => (
                      <SortableStageItem
                        key={stage.id}
                        stage={stage}
                        fundCount={fundCountByStage[stage.id] || 0}
                        onDelete={() => handleRemoveStage(stage.id)}
                        canDelete={fundCountByStage[stage.id] === 0}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>

              <div className="su-custom-stage-add" style={{ marginTop: '12px' }}>
                <input
                  type="text"
                  className="su-custom-stage-add-input"
                  placeholder="New stage name..."
                  value={newStageName}
                  onChange={(e) => setNewStageName(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <button
                  type="button"
                  className="su-custom-stage-add-btn"
                  onClick={handleAddStage}
                  disabled={!newStageName.trim()}
                >
                  <PlusIcon width={18} height={18} />
                </button>
              </div>
            </div>
          </div>

          <div className="su-fundraise-modal-footer">
            <button
              type="button"
              className="su-btn-secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="su-btn-primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Done'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, document.body);
};

export default EditPipelineModal;
